import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import type { SessionActivityResponse } from '~/server_shared/openapi/data-contracts';

import CardDialog from '../CardDialog';

import { publishSessionExpiresAt } from './userPresence';
import useUserPresenceReporting from './useUserPresenceReporting';

const WARNING_SECONDS_BEFORE_EXPIRY = 120;

interface SessionTimeoutWarningProps {
  sessionExpiresAt: number | null;
  isCallInProgress: boolean;
  onSessionRefreshed: (newExpiresAt: number | null) => void;
  onSessionExpired: () => void;
  onWarningStateChange: (isWarningActive: boolean) => void;
}

type ModalState = 'hidden' | 'warning' | 'expired';

const formatCountdown = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  sessionExpiresAt,
  isCallInProgress,
  onSessionRefreshed,
  onSessionExpired,
  onWarningStateChange,
}) => {
  const [modalState, setModalState] = useState<ModalState>('hidden');
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const prevWarningActiveRef = useRef(false);

  // While a dialog is up, only "Stay Logged In" extends the session.
  useUserPresenceReporting({
    isEnabled: !!sessionExpiresAt,
    isPaused: modalState !== 'hidden',
    isCallInProgress,
    onSessionRefreshed,
  });

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Notify parent when warning state changes
  useEffect(() => {
    const isWarningActive = modalState === 'warning' || modalState === 'expired';
    if (isWarningActive !== prevWarningActiveRef.current) {
      prevWarningActiveRef.current = isWarningActive;
      onWarningStateChange(isWarningActive);
    }
  }, [modalState, onWarningStateChange]);

  // Main timer effect
  useEffect(() => {
    if (!sessionExpiresAt) {
      setModalState('hidden');
      clearTimer();
      return;
    }

    const tick = () => {
      const nowSeconds = Date.now() / 1000;
      const secondsLeft = Math.max(0, Math.round(sessionExpiresAt - nowSeconds));

      if (secondsLeft <= 0) {
        setModalState('expired');
        setRemainingSeconds(0);
        clearTimer();
        onSessionExpired();
        return true; // expired
      }

      if (secondsLeft <= WARNING_SECONDS_BEFORE_EXPIRY) {
        setModalState('warning');
        setRemainingSeconds(secondsLeft);
      } else {
        setModalState('hidden');
      }
      return false;
    };

    // Initial check - don't start interval if already expired
    const alreadyExpired = tick();
    if (alreadyExpired) return;

    // Set up interval - check every second
    clearTimer();
    intervalRef.current = setInterval(tick, 1000);

    return clearTimer;
  }, [sessionExpiresAt, clearTimer, onSessionExpired]);

  const handleStayLoggedIn = async () => {
    clearTimer();
    setIsRefreshing(true);
    try {
      const response = await axios.post<SessionActivityResponse>('/api/v1/users/activity');
      if (!mountedRef.current) return;
      const newExpiresAt = response.data?.session_expires_at;
      if (newExpiresAt) {
        publishSessionExpiresAt(newExpiresAt);
        setModalState('hidden');
        onSessionRefreshed(newExpiresAt);
      } else {
        // Session refreshed but timeout no longer configured
        setModalState('hidden');
        onSessionRefreshed(null);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Session truly expired
        setModalState('expired');
        onSessionExpired();
      } else {
        // Network error or other transient failure - hide modal and let polling resume
        setModalState('hidden');
      }
    } finally {
      if (mountedRef.current) {
        setIsRefreshing(false);
      }
    }
  };

  const handleLoginAgain = () => {
    window.open('/api/logout', '_self');
  };

  if (modalState === 'hidden') {
    return null;
  }

  if (modalState === 'expired') {
    return (
      <CardDialog isDialog open title="Session Expired" preventClose>
        <div className="flex flex-col items-center gap-4 py-2">
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="text-center">
            Your session has expired due to inactivity.
          </Text>
          <div className="flex justify-center pt-2">
            <Button variant="contained" color="primary" onClick={handleLoginAgain}>
              Log In Again
            </Button>
          </div>
        </div>
      </CardDialog>
    );
  }

  return (
    <CardDialog isDialog open title="Session Expiring Soon" preventClose>
      <div className="flex flex-col items-center gap-4 py-2">
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="text-center">
          Your session will expire due to inactivity in:
        </Text>
        <Text
          variant={Text.VARIANTS.XL}
          weight={Text.WEIGHTS.SEMI_BOLD}
          colorVariant={Text.COLOR_VARIANTS.ERROR}
          className="tabular-nums"
        >
          {formatCountdown(remainingSeconds)}
        </Text>
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="text-center">
          Click below to stay logged in.
        </Text>
        <div className="flex justify-center pt-2">
          <Button variant="contained" color="primary" onClick={handleStayLoggedIn} disabled={isRefreshing}>
            {isRefreshing ? 'Refreshing...' : 'Stay Logged In'}
          </Button>
        </div>
      </div>
    </CardDialog>
  );
};

export default SessionTimeoutWarning;
